<template>
  <section class="hero-section">
    <!-- Decorative background glow -->
    <div class="hero-glow hero-glow-1"></div>
    <div class="hero-glow hero-glow-2"></div>
    <div class="hero-grid-pattern"></div>

    <v-container class="hero-container py-8 py-md-14">
      <!-- HERO ARCHITECTURAL GRID -->
      <!-- On Desktop (md+): Left text (order-md-1) & Right showcase (order-md-2) sit side-by-side; Search bar (order-md-3) sits full-width below them -->
      <!-- On Mobile (< md): Stacks vertically as: 1. Left text (order-1) -> 2. Search bar (order-2) -> 3. Showcase column (order-3) -->
      <v-row class="align-center">
        <!-- Left content -->
        <v-col cols="12" md="7" lg="7" class="text-left z-index-1 order-1 order-md-1">
          <div class="d-inline-flex align-center badge-pill mb-4">
            <v-icon color="primary" size="small" class="mr-2">mdi-home-search-outline</v-icon>
            <span class="text-caption font-weight-bold text-uppercase tracking-wider">
              Gestor Inmobiliario
            </span>
          </div>

          <h1 class="hero-title mb-4">
            Encuentre su Inmueble en <span class="text-primary-glow">Venta</span> o <span class="text-primary-glow">Arriendo</span>
          </h1>

          <p class="hero-subtitle mb-8">
            Líderes consolidados en finca raíz en el norte de Cundinamarca. Conectamos propiedades excepcionales con clientes idóneos con más de una década de trayectoria y seguridad predial.
          </p>

          <!-- Trust points -->
          <div class="d-flex flex-wrap align-center trust-points">
            <div class="d-flex align-center mr-6 mb-3">
              <v-icon color="primary" size="small" class="mr-2">mdi-check-decagram</v-icon>
              <span class="text-body-2 text-grey-lighten-2 font-weight-medium">Desde 2009</span>
            </div>
            <div class="d-flex align-center mb-3">
              <v-icon color="primary" size="small" class="mr-2">mdi-shield-check-outline</v-icon>
              <span class="text-body-2 text-grey-lighten-2 font-weight-medium">Estudio de Títulos & Seguridad</span>
            </div>
          </div>
        </v-col>

        <!-- Right visual column (Showcase cards: appears after search bar on mobile, right side on desktop) -->
        <v-col cols="12" md="5" lg="5" class="d-flex justify-center z-index-1 mt-8 mt-md-0 order-3 order-md-2">
          <div class="hero-showcase">
            <!-- Top badge: Experience (In document flow above main card, never overlaps) -->
            <div class="d-flex justify-end mb-4">
              <div class="showcase-card badge-card badge-top-right">
                <v-avatar color="primary" size="40" class="mr-3 shadow-sm">
                  <v-icon color="white" size="20">mdi-trophy-award</v-icon>
                </v-avatar>
                <div class="text-left">
                  <div class="text-subtitle-1 font-weight-black text-white line-height-1">+17 Años</div>
                  <div class="text-caption text-grey-lighten-2">Trayectoria Sólida</div>
                </div>
              </div>
            </div>

            <!-- Main card: Real estate leadership -->
            <div class="showcase-card main-card">
              <div class="d-flex align-center justify-space-between mb-5 flex-wrap gap-2">
                <div class="d-flex align-center">
                  <div class="icon-box primary-bg mr-3">
                    <v-icon color="white" size="24">mdi-home-group</v-icon>
                  </div>
                  <div>
                    <div class="text-subtitle-1 font-weight-bold text-white">León & Casallas</div>
                    <div class="text-caption text-grey-lighten-2">Finca Raíz & Consultoría</div>
                  </div>
                </div>
                <v-chip size="small" color="primary" variant="flat" class="font-weight-bold">
                  Sede Gachancipá
                </v-chip>
              </div>

              <v-divider class="border-opacity-25 mb-5"></v-divider>

              <div class="d-flex align-center mb-4">
                <v-icon color="primary" size="20" class="mr-3 flex-shrink-0">mdi-check-circle</v-icon>
                <span class="text-body-2 text-grey-lighten-1">Venta de casas, apartamentos, fincas y lotes</span>
              </div>
              <div class="d-flex align-center mb-4">
                <v-icon color="primary" size="20" class="mr-3 flex-shrink-0">mdi-check-circle</v-icon>
                <span class="text-body-2 text-grey-lighten-1">Arrendamientos residenciales e industriales</span>
              </div>
              <div class="d-flex align-center mb-6">
                <v-icon color="primary" size="20" class="mr-3 flex-shrink-0">mdi-check-circle</v-icon>
                <span class="text-body-2 text-grey-lighten-1">Seguridad predial en el norte de Cundinamarca</span>
              </div>

              <v-btn
                block
                variant="outlined"
                color="white"
                size="default"
                href="https://wa.me/573112160295"
                target="_blank"
                class="font-weight-bold whatsapp-btn"
              >
                <v-icon left size="18" class="mr-2 text-primary">mdi-whatsapp</v-icon>
                Asesoría Directa por WhatsApp
              </v-btn>
            </div>

            <!-- Bottom badge: Coverage (In document flow below main card, never overlaps) -->
            <div class="d-flex justify-start mt-4">
              <div class="showcase-card badge-card badge-bottom-left">
                <v-avatar color="white" size="40" class="mr-3 shadow-sm">
                  <v-icon color="primary" size="20">mdi-home-city</v-icon>
                </v-avatar>
                <div class="text-left">
                  <div class="text-subtitle-2 font-weight-black text-white line-height-1">7 Municipios</div>
                  <div class="text-caption text-grey-lighten-2">Norte de Cundinamarca</div>
                </div>
              </div>
            </div>
          </div>
        </v-col>
        <!-- FULL-WIDTH ARCHITECTURAL PROPERTY SEARCH BAR (order-2 on mobile, order-md-3 on desktop) -->
        <v-col v-if="$store.getters.isPropertiesSearchEnabled" cols="12" class="z-index-2 mt-6 mt-md-10 order-2 order-md-3">
          <div class="search-box-card pa-5 pa-md-7">
            <!-- Header bar with operation pill tabs & title -->
            <div class="d-flex align-center justify-space-between flex-wrap gap-4 mb-5 pb-4 border-bottom-subtle">
              <div class="d-flex align-center flex-wrap gap-3">
                <div class="d-flex align-center mr-2">
                  <div class="search-icon-badge mr-3">
                    <v-icon color="white" size="22">mdi-home-search</v-icon>
                  </div>
                  <div>
                    <span class="text-h6 font-weight-black text-white d-block line-height-1">
                      Búsqueda Rápida de Propiedades
                    </span>
                    <span class="text-caption text-grey-lighten-2">
                      Filtre por municipio, tipo de inmueble y habitaciones
                    </span>
                  </div>
                </div>

                <!-- Operation toggle tabs (Todos, Venta, Arriendo) -->
                <div class="d-flex gap-2">
                  <v-btn
                    v-for="op in operations"
                    :key="op"
                    size="small"
                    :color="filter.operation === op ? 'primary' : 'grey-darken-3'"
                    :variant="filter.operation === op ? 'flat' : 'outlined'"
                    class="font-weight-bold px-5 rounded-pill"
                    @click="filter.operation = op"
                  >
                    {{ op }}
                  </v-btn>
                </div>
              </div>

              <div class="d-flex align-center">
                <v-btn
                  size="small"
                  color="primary"
                  variant="flat"
                  href="https://wa.me/573112160295?text=Hola,%20deseo%20publicar%20mi%20inmueble%20con%20Le%C3%B3n%20%26%20Casallas"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-weight-bold px-4 py-1 rounded-pill publish-brand-btn text-white"
                >
                  <v-icon left size="18" class="mr-1">mdi-whatsapp</v-icon>
                  PUBLICA CON NOSOTROS
                </v-btn>
              </div>
            </div>

            <!-- Filter controls: Proportional 4-column horizontal row on Desktop/Wide resolutions -->
            <v-row dense class="align-center">
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="filter.city"
                  :items="cityOptions"
                  label="Municipio / Ciudad"
                  variant="outlined"
                  density="comfortable"
                  bg-color="rgba(255, 255, 255, 0.07)"
                  color="primary"
                  hide-details
                  prepend-inner-icon="mdi-map-marker-outline"
                  class="search-select"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="filter.type"
                  :items="typeOptions"
                  label="Tipo de Inmueble"
                  variant="outlined"
                  density="comfortable"
                  bg-color="rgba(255, 255, 255, 0.07)"
                  color="primary"
                  hide-details
                  prepend-inner-icon="mdi-home-city-outline"
                  class="search-select"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="filter.bedrooms"
                  :items="bedroomOptions"
                  label="Habitaciones"
                  variant="outlined"
                  density="comfortable"
                  bg-color="rgba(255, 255, 255, 0.07)"
                  color="primary"
                  hide-details
                  prepend-inner-icon="mdi-bed-empty"
                  class="search-select"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-btn
                  block
                  color="primary"
                  height="48"
                  elevation="6"
                  class="search-submit-btn font-weight-black text-body-1"
                  @click="handleSearch"
                >
                  <v-icon left size="22" class="mr-2">mdi-magnify</v-icon>
                  BUSCAR AHORA
                  <v-icon right size="18" class="ml-2">mdi-arrow-right</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
import { CITIES, PROPERTY_TYPES } from '@/data/properties';

export default {
  name: 'HeroSection',
  emits: ['scroll-to-services'],
  data() {
    return {
      operations: ['Todos', 'Venta', 'Arriendo'],
      cityOptions: ['Cualquiera', ...CITIES],
      typeOptions: ['Cualquiera', ...PROPERTY_TYPES],
      bedroomOptions: ['Cualquiera', '1+', '2+', '3+', '4+'],
      filter: {
        operation: 'Todos',
        city: 'Cualquiera',
        type: 'Cualquiera',
        bedrooms: 'Cualquiera',
      },
    };
  },
  methods: {
    handleSearch() {
      const query = {};
      if (this.filter.operation !== 'Todos') {
        query.operation = this.filter.operation;
      }
      if (this.filter.city !== 'Cualquiera') {
        query.city = this.filter.city;
      }
      if (this.filter.type !== 'Cualquiera') {
        query.type = this.filter.type;
      }
      if (this.filter.bedrooms !== 'Cualquiera') {
        query.bedrooms = this.filter.bedrooms;
      }
      this.$router.push({ name: 'properties', query });
    },
  },
};
</script>

<style scoped>
.hero-section {
  position: relative;
  background: radial-gradient(circle at 80% 20%, #1c1416 0%, #111113 60%, #0c0c0e 100%);
  color: #ffffff;
  overflow: hidden;
  min-height: 82vh;
  display: flex;
  align-items: center;
}

.hero-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  pointer-events: none;
  opacity: 0.28;
  z-index: 0;
}

.hero-glow-1 {
  top: -10%;
  right: 5%;
  width: 420px;
  height: 420px;
  background: #A90707;
}

.hero-glow-2 {
  bottom: -15%;
  left: 10%;
  width: 350px;
  height: 350px;
  background: #A90707;
}

.hero-grid-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 32px 32px;
  z-index: 0;
}

.hero-container {
  position: relative;
  z-index: 2;
  max-width: 1320px !important;
}

.badge-pill {
  background: rgba(169, 7, 7, 0.15);
  border: 1px solid rgba(169, 7, 7, 0.45);
  padding: 6px 16px;
  border-radius: 50px;
  backdrop-filter: blur(10px);
}

.tracking-wider {
  letter-spacing: 0.08em;
  color: #f3f4f6;
}

.hero-title {
  font-size: clamp(2.2rem, 4.2vw, 3.6rem);
  font-weight: 900;
  line-height: 1.15;
  letter-spacing: -0.02em;
}

.text-primary-glow {
  color: #A90707;
  text-shadow: 0 0 25px rgba(169, 7, 7, 0.45);
}

.hero-subtitle {
  font-size: clamp(1rem, 1.6vw, 1.18rem);
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.82);
  max-width: 620px;
}

/* ARCHITECTURAL FULL-WIDTH SEARCH BOX CARD STYLES */
.search-box-card {
  background: rgba(22, 22, 26, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 24px;
  backdrop-filter: blur(24px);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.65);
  width: 100%;
  transition: all 0.3s ease;
}

.search-box-card:hover {
  border-color: rgba(169, 7, 7, 0.45);
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.75);
}

.search-icon-badge {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #A90707;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(169, 7, 7, 0.4);
}

.border-bottom-subtle {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}
.gap-4 {
  gap: 16px;
}

.search-select :deep(.v-field) {
  color: white !important;
  border-color: rgba(255, 255, 255, 0.25) !important;
  border-radius: 12px;
}

.search-select :deep(.v-field__input) {
  font-size: 0.95rem;
  font-weight: 500;
}

.search-submit-btn {
  background: #A90707 !important;
  color: #ffffff !important;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(169, 7, 7, 0.55) !important;
}

/* Showcase right side */
.hero-showcase {
  position: relative;
  width: 100%;
  max-width: 450px;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
}

.showcase-card {
  background: rgba(26, 26, 30, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.showcase-card:hover {
  transform: translateY(-5px);
  border-color: rgba(169, 7, 7, 0.55);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.65);
}

.main-card {
  padding: 28px 24px;
  position: relative;
  z-index: 2;
}

.badge-card {
  padding: 10px 18px;
  display: inline-flex;
  align-items: center;
  border-radius: 50px;
  animation: float-animation 6s ease-in-out infinite;
}

.badge-top-right {
  animation-delay: 0s;
}

.badge-bottom-left {
  animation-delay: 3s;
}

.icon-box {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.primary-bg {
  background: #A90707;
  box-shadow: 0 4px 14px rgba(169, 7, 7, 0.4);
}

.whatsapp-btn {
  border-color: rgba(255, 255, 255, 0.35) !important;
  transition: all 0.3s ease;
}

.whatsapp-btn:hover {
  background: rgba(255, 255, 255, 0.08) !important;
  border-color: #A90707 !important;
  box-shadow: 0 8px 25px rgba(169, 7, 7, 0.45);
}

.publish-brand-btn {
  background: #A90707 !important;
  color: #FFFFFF !important;
  transition: all 0.3s ease;
  box-shadow: 0 4px 14px rgba(169, 7, 7, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.publish-brand-btn:hover {
  background: #C40808 !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(169, 7, 7, 0.65);
  border-color: rgba(255, 255, 255, 0.4);
}

.line-height-1 {
  line-height: 1.15;
}

.shadow-sm {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
}

@keyframes float-animation {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
}

@media (max-width: 960px) {
  .hero-showcase {
    max-width: 380px;
    margin: 20px auto 0;
  }
}
</style>
